const dayjs = require('dayjs');
const {
    lang: { LANG_ENUM }
} = require('@futuweb/fego-bridge');
const { use } = require('@fegobull/webapi');

/**
 * 获取当前语言
 */
export const getCurrentLang = () => {
    const { useLang } = use;
    const { lang } = useLang();
    return lang;
};

/**
 * 从 LocalText[] 中获取当前语言的文本
 * @param {Array} localTextList - LocalText 数组
 * @param {string} key - 文本键名，如 'name', 'placeholder'
 * @returns {string} 当前语言的文本
 */
export const getLocalText = (localTextList, key) => {
    if (!localTextList || !Array.isArray(localTextList) || localTextList.length === 0) {
        return '';
    }

    const lang = getCurrentLang();
    const textItem = localTextList.find((item) => item.key === key);

    if (!textItem) {
        return '';
    }

    // 根据语言返回对应的文本
    if (lang === LANG_ENUM.CN) {
        return textItem.sc || '';
    }
    if (lang === LANG_ENUM.HK) {
        return textItem.tc || textItem.sc || '';
    }
    if (lang === LANG_ENUM.EN) {
        return textItem.en || '';
    }
    if (lang === LANG_ENUM.JA) {
        return textItem.jp || '';
    }

    // 默认返回简体中文
    return textItem.sc || '';
};

/**
 * 从 FormOption[] 中获取当前语言的文本
 * @param {Object} formOption - FormOption 对象
 * @returns {string} 当前语言的文本
 */
export const getFormOptionText = (formOption) => {
    if (!formOption) {
        return '';
    }

    const lang = getCurrentLang();

    if (lang === LANG_ENUM.CN) {
        return formOption.sc || '';
    }
    if (lang === LANG_ENUM.HK) {
        return formOption.tc || formOption.sc || '';
    }
    if (lang === LANG_ENUM.EN) {
        return formOption.en || '';
    }
    if (lang === LANG_ENUM.JA) {
        return formOption.jp || '';
    }

    // 默认返回简体中文
    return formOption.sc || '';
};

/**
 * 判断当前语言是否为英文
 * @returns {boolean}
 */
export const isEnglish = () => {
    const lang = getCurrentLang();
    return lang === LANG_ENUM.EN;
};

/**
 * 格式化日期（根据语言自动选择格式）
 * @param {string} dateStr - 日期字符串，格式：YYYY-MM-DD，如 '2025-01-15'
 * @returns {string} 格式化后的日期
 *   - 英文：DD/MM/YYYY（如 '15/01/2025'）
 *   - 其他语言：YYYY/MM/DD（如 '2025/01/15'）
 */
export const formatDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) {
        return '';
    }

    const [year, month, day] = dateStr.split('-');

    if (isEnglish()) {
        // 英文：DD/MM/YYYY
        return `${day}/${month}/${year}`;
    }
    // 其他语言（中文、繁体、日文）：YYYY/MM/DD
    return `${year}/${month}/${day}`;
};

/**
 * 格式化日期（短格式，仅月日，跨年时展示年份）
 * @param {string} dateStr - 日期字符串，格式：YYYY-MM-DD，如 '2025-01-15'
 * @returns {string} 格式化后的日期
 *   - 中文/繁体/日文（本年度）：1月15日
 *   - 中文/繁体/日文（非本年度）：2027年1月15日
 *   - 英文（本年度）：15/01
 *   - 英文（非本年度）：15/01/2027
 */
export const formatDateShort = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) {
        return '';
    }

    const [year, month, day] = dateStr.split('-');
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    const isCurrentYear = parseInt(year, 10) === dayjs().year();

    if (isEnglish()) {
        // 英文：DD/MM 或 DD/MM/YYYY
        if (isCurrentYear) {
            return `${day}/${month}`;
        }
        return `${day}/${month}/${year}`;
    }

    // 其他语言（中文、繁体、日文）：M月D日 或 YYYY年M月D日
    if (isCurrentYear) {
        return `${monthNum}月${dayNum}日`;
    }
    return `${year}年${monthNum}月${dayNum}日`;
};

/**
 * 格式化时段
 * @param {string} rangeKey - 时段标识，格式：HHmm_HHmm，如 '0900_1200'
 * @param {number} available - 剩余可预约名额（已废弃，不再显示）
 * @returns {string} 格式化后的时段，如 '09:00 - 12:00'
 */
export const formatTimeRange = (rangeKey, available) => {
    if (!rangeKey || rangeKey.length !== 9) {
        return '';
    }

    const start = rangeKey.substring(0, 4);
    const end = rangeKey.substring(5, 9);

    const startHour = start.substring(0, 2);
    const startMin = start.substring(2, 4);
    const endHour = end.substring(0, 2);
    const endMin = end.substring(2, 4);

    return `${startHour}:${startMin} - ${endHour}:${endMin}`;
};

/**
 * 判断日期是否是今天
 * @param {string} dateStr - 日期字符串，格式：YYYY-MM-DD，如 '2025-01-15'
 * @returns {boolean}
 */
export const isToday = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) {
        return false;
    }
    const date = dayjs(dateStr);
    return date.isSame(dayjs(), 'day');
};

/**
 * 判断日期是否是明天
 * @param {string} dateStr - 日期字符串，格式：YYYY-MM-DD，如 '2025-01-15'
 * @returns {boolean}
 */
export const isTomorrow = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) {
        return false;
    }
    const date = dayjs(dateStr);
    return date.isSame(dayjs().add(1, 'day'), 'day');
};

/**
 * 获取星期几（多语言）
 * @param {string} dateStr - 日期字符串，格式：YYYY-MM-DD，如 '2025-01-15'
 * @returns {string} 星期几的文本
 */
export const getWeekday = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) {
        return '';
    }
    const date = dayjs(dateStr);
    const weekday = date.day(); // 0=周日, 1=周一, ..., 6=周六

    const lang = getCurrentLang();
    const weekdays = {
        [LANG_ENUM.CN]: ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'],
        [LANG_ENUM.HK]: ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'],
        [LANG_ENUM.EN]: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        [LANG_ENUM.JA]: ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日']
    };

    return weekdays[lang]?.[weekday] || weekdays[LANG_ENUM.CN][weekday];
};

/**
 * 格式化月份标签
 * @param {string} monthStr - 月份字符串，格式：YYYY-MM，如 '2025-01'
 * @returns {string} 格式化后的月份，如 '5月' 或 '2025年5月'（如果不是本年度）
 */
export const formatMonthLabel = (monthStr) => {
    if (!monthStr || monthStr.length !== 7) {
        return '';
    }
    const [year, monthPart] = monthStr.split('-');
    const month = parseInt(monthPart, 10);
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1; // dayjs month 从 0 开始

    const lang = getCurrentLang();
    const isCurrentYear = parseInt(year, 10) === currentYear;

    if (lang === LANG_ENUM.CN || lang === LANG_ENUM.HK) {
        if (isCurrentYear) {
            return `${month}月`;
        }
        return `${year}年${month}月`;
    }
    if (lang === LANG_ENUM.EN) {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        if (isCurrentYear) {
            return monthNames[month - 1];
        }
        return `${monthNames[month - 1]} ${year}`;
    }
    if (lang === LANG_ENUM.JA) {
        if (isCurrentYear) {
            return `${month}月`;
        }
        return `${year}年${month}月`;
    }

    // 默认返回中文格式
    if (isCurrentYear) {
        return `${month}月`;
    }
    return `${year}年${month}月`;
};

/**
 * 从日期列表中提取所有唯一的月份
 * @param {Array} availableDates - DateSlot 数组，每个 date 格式为 'YYYY-MM-DD'
 * @returns {Array<string>} 月份列表，格式：['YYYY-MM', ...]，已排序
 */
export const extractMonths = (availableDates) => {
    if (!availableDates || !Array.isArray(availableDates)) {
        return [];
    }

    const monthSet = new Set();
    availableDates.forEach((dateSlot) => {
        if (dateSlot.date && dateSlot.date.length === 10) {
            const month = dateSlot.date.substring(0, 7); // YYYY-MM
            monthSet.add(month);
        }
    });

    return Array.from(monthSet).sort();
};

/**
 * 根据月份过滤日期
 * @param {Array} availableDates - DateSlot 数组
 * @param {string} monthStr - 月份字符串，格式：YYYY-MM
 * @returns {Array} 该月的 DateSlot 数组，已按日期排序
 */
export const filterDatesByMonth = (availableDates, monthStr) => {
    if (!availableDates || !Array.isArray(availableDates) || !monthStr) {
        return [];
    }

    return availableDates
        .filter((dateSlot) => {
            if (!dateSlot.date || dateSlot.date.length !== 10) {
                return false;
            }
            return dateSlot.date.substring(0, 7) === monthStr;
        })
        .sort((a, b) => {
            // 按日期升序排序
            return a.date.localeCompare(b.date);
        });
};

/**
 * 获取日期的显示标签（今天、明天、星期几）
 * @param {string} dateStr - 日期字符串，格式：YYYY-MM-DD
 * @returns {string} 标签文本，如 '22(今天)' 或 '24(星期二)'
 */
export const getDateLabel = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) {
        return '';
    }
    const day = parseInt(dateStr.split('-')[2], 10);

    if (isToday(dateStr)) {
        const lang = getCurrentLang();
        const todayText = {
            [LANG_ENUM.CN]: '今天',
            [LANG_ENUM.HK]: '今天',
            [LANG_ENUM.EN]: 'Today',
            [LANG_ENUM.JA]: '今日'
        };
        return `${day}(${todayText[lang] || todayText[LANG_ENUM.CN]})`;
    }

    if (isTomorrow(dateStr)) {
        const lang = getCurrentLang();
        const tomorrowText = {
            [LANG_ENUM.CN]: '明天',
            [LANG_ENUM.HK]: '明天',
            [LANG_ENUM.EN]: 'Tomorrow',
            [LANG_ENUM.JA]: '明日'
        };
        return `${day}(${tomorrowText[lang] || tomorrowText[LANG_ENUM.CN]})`;
    }

    const weekday = getWeekday(dateStr);
    return `${day}(${weekday})`;
};

/**
 * 对预约记录列表进行排序
 * 排序规则：
 * 1. 未结束（nowUnix <= sign_end）：按 activity_start 升序（最近的排前面）
 * 2. 已结束（nowUnix > sign_end）：按 activity_start 降序（最近结束的排前面）
 * 展示顺序：[...未结束, ...已结束]
 *
 * @param {Array} list - 预约记录列表，每条记录需包含 sign_end 和 activity_start 字段
 * @returns {Array} 排序后的新列表（不修改原数组）
 */
export const sortReservationList = (list) => {
    if (!list || !Array.isArray(list) || list.length === 0) {
        return [];
    }

    const nowUnix = Math.floor(Date.now() / 1000);
    const notEnded = [];
    const ended = [];

    for (const record of list) {
        const signEnd = record.sign_end ?? 0;
        if (nowUnix <= signEnd) {
            notEnded.push(record);
        } else {
            ended.push(record);
        }
    }

    // 未结束：按 activity_start 升序（最近的活动排前面）
    notEnded.sort((a, b) => (a.activity_start ?? 0) - (b.activity_start ?? 0));
    // 已结束：按 activity_start 降序（最近结束的排前面）
    ended.sort((a, b) => (b.activity_start ?? 0) - (a.activity_start ?? 0));

    return [...notEnded, ...ended];
};
